import type { ColumnDef } from '@tanstack/react-table'
import type { OrganisationUser } from '@/api/resources/organisation-users'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { organisationUsersQueryOptions } from '@/api/resources/organisation-users'
import { DataTable } from '@/components/data-table/data-table'
import { OrganisationNavSelect } from '@/components/layout/organisation-nav-select'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

export const Route = createFileRoute('/_app/users/$organisationId/system-managers')({
  head: () => ({ meta: [{ title: 'Users - MyJunea' }] }),
  component: SystemManagersPage,
})

const columns: ColumnDef<OrganisationUser, unknown>[] = [
  {
    accessorKey: 'first_name',
    header: 'Name',
    cell: ({ row }) => `${row.original.first_name} ${row.original.last_name}`,
  },
  {
    accessorKey: 'email',
    header: 'Email',
  },
  {
    accessorKey: 'is_contact',
    header: 'Contact',
    cell: ({ row }) => (row.original.is_contact ? <Badge>Contact</Badge> : null),
  },
]

function SystemManagersPage(): React.ReactElement {
  const { organisationId } = Route.useParams()
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })

  const organisationIdNum = Number(organisationId)

  const { data: users, isLoading } = useQuery({
    ...organisationUsersQueryOptions(organisationIdNum, {
      search: debouncedSearch || undefined,
    }),
    placeholderData: keepPreviousData,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Manage system managers and operators."
        defaultFiltersOpen={true}
        action={
          <Button asChild>
            <Link
              to="/users/$organisationId/system-managers/new"
              params={{ organisationId }}
              resetScroll={false}
            >
              Add system manager
            </Link>
          </Button>
        }
        filters={
          <>
            <OrganisationNavSelect
              currentOrganisationId={organisationIdNum}
              baseRoute="/users/$organisationId/system-managers"
            />
            <SearchInput
              placeholder="Search users..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="w-48"
            />
          </>
        }
      />

      <Tabs value="users">
        <TabsList>
          <TabsTrigger value="users" asChild>
            <Link
              to="/users/$organisationId/system-managers"
              params={{ organisationId }}
              resetScroll={false}
            >
              System managers
            </Link>
          </TabsTrigger>
          <TabsTrigger value="operators" asChild>
            <Link
              to="/users/$organisationId/operators"
              params={{ organisationId }}
              resetScroll={false}
            >
              Operators
            </Link>
          </TabsTrigger>
        </TabsList>
        <p className="text-muted-foreground mt-3 max-w-3xl text-sm text-pretty">
          Everyone you add here becomes an admin: they sign in to MyJunea and manage organisations,
          machines, prices, users, and reports. Remove someone and they lose that access but keep
          their Junea Wallet.
        </p>

        <div className="mt-4">
          <DataTable
            columns={columns}
            data={users ?? []}
            isLoading={isLoading}
            onRowClick={(user) =>
              void navigate({
                to: '/users/$organisationId/system-managers/$userId/edit',
                params: { organisationId, userId: String(user.id) },
                resetScroll: false,
              })
            }
            emptyMessage="No users found."
            renderMobileCard={(user) => (
              <div className="bg-card rounded-xl border p-4">
                <div className="font-medium">
                  {user.first_name} {user.last_name}
                </div>
                <div className="text-muted-foreground text-sm">{user.email}</div>
                {user.is_contact && (
                  <div className="mt-2 flex gap-2">
                    <Badge>Contact</Badge>
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </Tabs>

      <Outlet />
    </div>
  )
}
